import { useCallback } from 'react';
import { CalendarEvent } from '../useSupabaseEvents';
import { RecurrenceRule } from '../../components/calendar/dialogs/RecurrenceSelector';
import { generateRecurrenceInstances } from '../../utils/recurrence';
import { dateRangeToSelectedCells } from '../../utils/calendar/dateHelpers';
import { Calendar } from '../../types';

interface UseEventHandlersProps {
  // Supabase hooks
  saveEvent: (event: Omit<CalendarEvent, 'id'>) => Promise<CalendarEvent | undefined>;
  addEventBatch: (events: Omit<CalendarEvent, 'id'>[]) => Promise<CalendarEvent[] | undefined>;
  modifyEvent: (id: string | number, updates: Partial<CalendarEvent>) => Promise<any>;
  removeEvent: (id: string | number) => Promise<void>;
  deleteEventBatch: (ids: (string | number)[]) => Promise<void>;
  refetch: () => Promise<void>;

  // All events for recurring deletion
  events: CalendarEvent[];
}

interface EventFormData {
  title: string;
  notes: string;
  selectedCells: string[];
  startDate: string;
  endDate: string;
  selectedCalendar: Calendar | null;
  allDay: boolean;
  fullSize: boolean;
  startTime: string;
  endTime: string;
  recurrenceRule: RecurrenceRule;
}

export const useEventHandlers = ({
  saveEvent,
  addEventBatch,
  modifyEvent,
  removeEvent,
  deleteEventBatch,
  refetch,
  events,
}: UseEventHandlersProps) => {

  // Add a new event (with optional recurrence)
  const addEvent = useCallback(async (formData: EventFormData): Promise<CalendarEvent | false> => {
    const {
      title, notes, selectedCells, startDate, endDate,
      selectedCalendar, allDay, fullSize, startTime, endTime, recurrenceRule
    } = formData;

    let finalSelectedCells = selectedCells;

    if (startDate && endDate) {
      finalSelectedCells = dateRangeToSelectedCells(startDate, endDate);
    }

    if (!title.trim()) return false;
    if (finalSelectedCells.length === 0) return false;

    try {
      const baseEventData = {
        title,
        notes,
        dates: [...finalSelectedCells].sort(),
        calendar_id: selectedCalendar?.id || null,
        color: {
          bg: selectedCalendar?.color || 'bg-blue-500',
          gradient: selectedCalendar?.color || 'bg-blue-500',
          name: selectedCalendar?.name || 'Default',
        } as any,
        all_day: allDay,
        full_size: fullSize,
        start_time: !allDay ? startTime : null,
        end_time: !allDay ? endTime : null
      };

      if (recurrenceRule.frequency !== 'none') {
        // Create main event with recurrence rule
        const newEvent = await saveEvent({
          ...baseEventData,
          recurrence_rule: recurrenceRule
        });

        if (newEvent) {
          // Generate all recurring instances
          const instances = generateRecurrenceInstances(
            startDate,
            endDate,
            recurrenceRule
          );

          // Prepare batch of recurring events
          const recurringEvents = instances.map(instance => ({
            ...baseEventData,
            dates: instance.dates,
            parent_event_id: String(newEvent.id)
          }));

          // Create all at once
          if (recurringEvents.length > 0) {
            await addEventBatch(recurringEvents);
          }
          return newEvent;
        }
      } else {
        // Non-recurring event
        const created = await saveEvent(baseEventData);
        return created || false;
      }

      return false;
    } catch (error) {
      console.error('Error adding event:', error);
      return false;
    }
  }, [saveEvent, addEventBatch]);

  // Update an existing event
  const updateEvent = useCallback(async (
    editingEvent: CalendarEvent,
    formData: EventFormData
  ): Promise<boolean> => {
    const {
      title, notes, selectedCells, startDate, endDate,
      selectedCalendar, allDay, fullSize, startTime, endTime, recurrenceRule
    } = formData;

    let finalSelectedCells = selectedCells;

    if (startDate && endDate) {
      finalSelectedCells = dateRangeToSelectedCells(startDate, endDate);
    }

    if (!title.trim() || finalSelectedCells.length === 0) return false;

    try {
      const existingColor = editingEvent.color as any;
      const baseEventData = {
        title,
        notes,
        dates: [...finalSelectedCells].sort(),
        calendar_id: selectedCalendar?.id || null,
        color: selectedCalendar
          ? { bg: selectedCalendar.color, gradient: selectedCalendar.color, name: selectedCalendar.name } as any
          : existingColor || null,
        all_day: allDay,
        full_size: fullSize,
        start_time: !allDay ? startTime : null,
        end_time: !allDay ? endTime : null
      };

      const wasRecurring = editingEvent.recurrence_rule || editingEvent.parent_event_id;
      const isNowRecurring = recurrenceRule.frequency !== 'none';

      if (!wasRecurring && isNowRecurring) {
        // Converting non-recurring to recurring
        await modifyEvent(editingEvent.id, {
          ...baseEventData,
          recurrence_rule: recurrenceRule
        });

        // Generate and batch create recurring instances
        const instances = generateRecurrenceInstances(
          startDate,
          endDate,
          recurrenceRule
        );

        const recurringEvents = instances.map(instance => ({
          ...baseEventData,
          dates: instance.dates,
          parent_event_id: String(editingEvent.id)
        }));

        if (recurringEvents.length > 0) {
          await addEventBatch(recurringEvents);
        }
      } else {
        // Regular update
        await modifyEvent(editingEvent.id, baseEventData);
      }

      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      return false;
    }
  }, [modifyEvent, addEventBatch]);

  // Update all events in a recurring series (keeps each event's existing dates)
  const updateEventSeries = useCallback(async (
    editingEvent: CalendarEvent,
    formData: EventFormData
  ): Promise<boolean> => {
    const { title, notes, selectedCalendar, allDay, fullSize, startTime, endTime } = formData;

    try {
      const existingColor = editingEvent.color as any;
      const sharedFields = {
        title,
        notes,
        calendar_id: selectedCalendar?.id || null,
        color: selectedCalendar
          ? { bg: selectedCalendar.color, gradient: selectedCalendar.color, name: selectedCalendar.name } as any
          : existingColor || null,
        all_day: allDay,
        full_size: fullSize,
        start_time: !allDay ? startTime : null,
        end_time: !allDay ? endTime : null,
      };

      const parentId = editingEvent.parent_event_id || editingEvent.id;

      // All events in the series (parent + all children)
      const seriesIds = events
        .filter(e => e.id === parentId || e.parent_event_id === parentId)
        .map(e => e.id);

      await Promise.all(seriesIds.map(id => modifyEvent(id, sharedFields)));

      return true;
    } catch (error) {
      console.error('Error updating recurring series:', error);
      return false;
    }
  }, [events, modifyEvent]);

  // Delete a single event
  const deleteEvent = useCallback(async (eventId: number | string): Promise<boolean> => {
    try {
      await removeEvent(eventId);
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      return false;
    }
  }, [removeEvent]);

  // Delete an entire recurring series
  const deleteRecurringSeries = useCallback(async (event: CalendarEvent): Promise<boolean> => {
    try {
      const parentId = event.parent_event_id || event.id;

      // Collect all related event IDs
      const relatedEventIds = events
        .filter(e => e.id === parentId || e.parent_event_id === parentId)
        .map(e => e.id);

      // Delete all at once
      await deleteEventBatch(relatedEventIds);

      return true;
    } catch (error) {
      console.error('Error deleting recurring series:', error);
      return false;
    }
  }, [events, deleteEventBatch]);

  // Delete multiple events
  const deleteMultipleEvents = useCallback(async (eventIds: (string | number)[]): Promise<void> => {
    try {
      await Promise.all(eventIds.map(id => removeEvent(id)));
      await refetch();
    } catch (error) {
      console.error('Error deleting events:', error);
    }
  }, [removeEvent, refetch]);

  // Quick update (for drag/drop and resize)
  const quickUpdateEvent = useCallback(async (
    eventId: string | number,
    updates: Partial<CalendarEvent>
  ): Promise<void> => {
    try {
      await modifyEvent(eventId, updates);
    } catch (error) {
      console.error('Error updating event:', error);
    }
  }, [modifyEvent]);

  return {
    addEvent,
    updateEvent,
    updateEventSeries,
    deleteEvent,
    deleteRecurringSeries,
    deleteMultipleEvents,
    quickUpdateEvent,
  };
};
